import { useState } from 'react';

import ChoiceSelect from '@/dashboard/ui/ChoiceSelect';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import type { ClinicRosterDoctor } from '@/dashboard/types';

/**
 * DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1 (S1) -- the day board's
 * assign/reassign doctor picker. Mirrors the CancelReasonDialog mounting
 * idiom (DrawerShell overlay + a required selection before the confirm
 * unlocks). Options are the CLINIC'S ACTIVE roster served by the board read
 * itself (data.clinicDoctors -- server clinic-fenced and active-only; this
 * dialog renders what it is given and never fetches).
 *
 * FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "ASSIGNED DOCTORS NAME LIST"):
 * the name list now carries the roster's OWN specialty truth -- the same
 * wire fact the planning calendar's column header already renders
 * (ClinicRosterDoctor.specialty; APPOINTMENT-BOOK-V1 S1b) -- and a
 * specialty CUT over the same served roster. Display + client-side
 * narrowing only: no new read, no wire change, and the write path is
 * byte-identical. The cut renders ONLY when the roster actually carries
 * more than one distinct specialty (a uniform or unset roster gets no
 * filter control -- honest absence, LES-016), and "All specialties" is the
 * resting state so the full roster stays one glance away.
 *
 * Honesty bars:
 *   - An EMPTY roster renders the honest empty line, never a dead confirm
 *     (LES-016: no dead doors).
 *   - Re-picking the CURRENT doctor keeps the confirm locked (a no-op is
 *     not an assignment act; the server treats it idempotently anyway).
 *   - A specialty cut that hides the tentative pick clears it (the select
 *     never silently submits an option the operator can no longer see).
 *   - A blank specialty on the wire is never invented: the option label is
 *     the name alone, and such doctors ride every specialty cut ONLY under
 *     "All specialties" (narrowing to a named specialty shows exactly the
 *     doctors recorded under it).
 *   - No free text, no phone, no clinical field -- roster ids + names +
 *     the administrative specialty column only.
 */

export const ASSIGN_DIALOG_TITLE = 'Assign doctor';
export const ASSIGN_DIALOG_REASSIGN_TITLE = 'Change doctor';
export const ASSIGN_DIALOG_EYEBROW = 'Clinic roster';
export const ASSIGN_DIALOG_PICK_LABEL = 'Doctor';
export const ASSIGN_DIALOG_CONFIRM_COPY = 'Confirm doctor';
export const ASSIGN_DIALOG_KEEP_COPY = 'Keep unchanged';
export const ASSIGN_DIALOG_EMPTY_COPY =
  'No active doctors at this clinic - the roster must be set up first.';
export const ASSIGN_DIALOG_SPECIALTY_LABEL = 'Specialty';
export const ASSIGN_DIALOG_SPECIALTY_ALL = 'All specialties';
export const DOCTOR_CHANGE_REASONS = [
  { code: 'doctor_unavailable', label: 'Doctor unavailable' },
  { code: 'doctor_busy', label: 'Doctor busy' },
  { code: 'covering_doctor', label: 'Covering doctor' },
  { code: 'other_operational', label: 'Other operational reason' },
] as const;

/** Same served clinician name as the board; specialty remains a separate filter. */
export function assignDoctorOptionLabel(
  doctor: Pick<ClinicRosterDoctor, 'name' | 'specialty'>,
): string {
  return doctor.name;
}

/** Distinct recorded specialties on the served roster, name-ordered.
 *  Doctors with no recorded specialty contribute nothing (the filter never
 *  invents a bucket for absence). */
export function assignRosterSpecialties(
  doctors: readonly Pick<ClinicRosterDoctor, 'specialty'>[],
): string[] {
  const out = new Set<string>();
  for (const d of doctors) {
    const specialty = d.specialty?.trim();
    if (specialty) out.add(specialty);
  }
  return [...out].sort((a, b) => a.localeCompare(b));
}

/** The roster under one specialty cut. null = All specialties (served
 *  order, byte-identical to the unfiltered dialog). */
export function filterRosterBySpecialty<
  T extends Pick<ClinicRosterDoctor, 'specialty'>,
>(doctors: readonly T[], specialty: string | null): T[] {
  if (specialty === null) return [...doctors];
  return doctors.filter((d) => d.specialty?.trim() === specialty);
}

export interface AssignDoctorDialogProps {
  /** The clinic's ACTIVE roster (id + name + specialty), as served by the
   *  board read. */
  readonly doctors: readonly ClinicRosterDoctor[];
  /** The row's currently assigned doctor id (null = Unassigned). */
  readonly currentDoctorId: number | null;
  readonly patientName?: string;
  readonly appointmentLabel?: string;
  readonly currentDoctorName?: string | null;
  /** True while the assign mutation is in flight (controls disable). */
  readonly busy: boolean;
  readonly error?: string | null;
  readonly terminal?: boolean;
  /** Fires with the PICKED roster id; the caller runs the mutation. */
  readonly onConfirm: (doctorId: number, reasonCode?: string) => void;
  readonly onClose: () => void;
}

export default function AssignDoctorDialog({
  doctors,
  currentDoctorId,
  patientName,
  appointmentLabel,
  currentDoctorName,
  busy,
  error,
  terminal = false,
  onConfirm,
  onClose,
}: AssignDoctorDialogProps): JSX.Element {
  const [picked, setPicked] = useState<string>(
    currentDoctorId !== null ? String(currentDoctorId) : '',
  );
  const [reasonCode, setReasonCode] = useState('');
  // '' = All specialties (the resting state; the full roster).
  const [specialty, setSpecialty] = useState<string>('');
  const specialties = assignRosterSpecialties(doctors);
  const visibleDoctors = filterRosterBySpecialty(
    doctors,
    specialty === '' ? null : specialty,
  );
  const pickedId = picked === '' ? null : Number(picked);
  const pickedDoctor = doctors.find(doctor => doctor.id === pickedId);
  const isNoOp = pickedId === null || pickedId === currentDoctorId || pickedDoctor?.assignmentEligible !== true;

  return (
    <Dialog open onOpenChange={(open,details)=>{if(busy){details.cancel();return;}if(!open)onClose()}}>
    <DialogContent className="p1-confirm-doctor" showCloseButton={false} data-testid="assign-doctor-dialog">
      <DialogTitle>{ASSIGN_DIALOG_CONFIRM_COPY}</DialogTitle>

      {error ? <p role="alert">{error}</p> : null}
      {patientName ? <p data-testid="assign-doctor-patient">{patientName}</p> : null}
      {appointmentLabel ? <p data-testid="assign-doctor-appointment">{appointmentLabel}</p> : null}
      <p data-testid="assign-doctor-current">Current doctor: {currentDoctorName?.trim() || (currentDoctorId === null ? 'Unassigned' : 'Name unavailable')}</p>
      {doctors.length === 0 ? (
        <p className="assign-doctor__empty" data-testid="assign-doctor-empty">
          {ASSIGN_DIALOG_EMPTY_COPY}
        </p>
      ) : (
        <>
          {specialties.length > 1 ? (
            <label className="assign-doctor__field">
              {ASSIGN_DIALOG_SPECIALTY_LABEL}
              <select
                value={specialty}
                onChange={(e) => {
                  const next = e.target.value;
                  setSpecialty(next);
                  // The cut must never hide a tentative pick behind the
                  // confirm: a pick outside the narrowed list clears.
                  if (picked !== '') {
                    const stillVisible = filterRosterBySpecialty(
                      doctors,
                      next === '' ? null : next,
                    ).some((d) => String(d.id) === picked);
                    if (!stillVisible) setPicked('');
                  }
                }}
                disabled={busy || terminal}
                data-testid="assign-doctor-specialty"
              >
                <option value="">{ASSIGN_DIALOG_SPECIALTY_ALL}</option>
                {specialties.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="assign-doctor__field">
            {ASSIGN_DIALOG_PICK_LABEL}
            <ChoiceSelect label="Doctor" value={picked} onChange={setPicked} disabled={busy || terminal} options={[{value:'',label:'Choose a doctor'},...visibleDoctors.map(d=>({value:String(d.id),label:assignDoctorOptionLabel(d)+(d.assignmentEligible===true?'':' — identity not verified'),disabled:d.assignmentEligible!==true}))]}/>

          </label>
          {doctors.length > 0 && doctors.every(doctor => doctor.assignmentEligible !== true) ? (
            <p role="status">No doctor has a verified roster identity for assignment at this clinic.</p>
          ) : null}
          {currentDoctorId !== null && !isNoOp ? (
            <label className="assign-doctor__field">
              Reason for change
              <ChoiceSelect label="Reason for change" value={reasonCode} onChange={setReasonCode} disabled={busy || terminal} options={[{value:'',label:'Choose a reason'},...DOCTOR_CHANGE_REASONS.map(r=>({value:r.code,label:r.label}))]}/>

            </label>
          ) : null}
          <p>COC coordinates cover. Choose an available doctor from this clinic.</p>
        </>
      )}
      <DialogFooter>
        <div className="assign-doctor__actions">
          <Button
            size="sm"
            variant="outline" className="ac-btn ac-btn--secondary"
            disabled={busy}
            onClick={onClose}
            data-testid="assign-doctor-keep"
          >
            {terminal ? 'Close' : ASSIGN_DIALOG_KEEP_COPY}
          </Button>
          {doctors.length > 0 ? (
            <Button
              size="sm"
              className="ac-btn ac-btn--primary"
              disabled={busy || terminal || isNoOp || (currentDoctorId !== null && reasonCode === '')}
              onClick={() => {
                if (!terminal && pickedId !== null && !isNoOp) onConfirm(pickedId, reasonCode || undefined);
              }}
              data-testid="assign-doctor-confirm"
            >
              {busy ? 'Assigning...' : ASSIGN_DIALOG_CONFIRM_COPY}
            </Button>
          ) : null}

        </div></DialogFooter>
    </DialogContent>
    </Dialog>
  );
}
